/**
 * Assistant Message Component
 *
 * Renders a single message in the assistant chat with markdown support,
 * code blocks, and tool call displays.
 */
import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { User, Bot, Copy, Check, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Message } from '@/contexts/GlobalAssistantContext';
import { ToolCallDisplay } from './ToolCallDisplay';

interface AssistantMessageProps {
  message: Message;
  onApplyCode?: (code: string) => void;
}

export function AssistantMessage({ message, onApplyCode }: AssistantMessageProps) {
  const [copiedId, setCopiedId] = React.useState<string | null>(null);
  const codeBlockCounter = React.useRef(0);

  // Reset counter on each render to ensure consistent IDs
  codeBlockCounter.current = 0;

  const handleCopy = async (content: string, id: string) => {
    await navigator.clipboard.writeText(content);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const isYamlOrMarkdown = (language: string, content: string) => {
    return (
      language === 'yaml' ||
      language === 'yml' ||
      language === 'markdown' ||
      language === 'md' ||
      content.includes('---\n') // Frontmatter indicator
    );
  };

  return (
    <div
      className={cn(
        'flex gap-3 w-full max-w-full',
        message.role === 'user' ? 'flex-row-reverse' : 'flex-row'
      )}
    >
      {/* Avatar */}
      <div
        className={cn(
          'flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center',
          message.role === 'user'
            ? 'bg-primary text-primary-foreground'
            : 'bg-purple-100 text-purple-600 dark:bg-purple-900 dark:text-purple-300'
        )}
      >
        {message.role === 'user' ? (
          <User className="h-4 w-4" />
        ) : (
          <Bot className="h-4 w-4" />
        )}
      </div>

      {/* Message content */}
      <div
        className={cn(
          'flex-1 space-y-2 min-w-0 overflow-hidden',
          message.role === 'user' ? 'text-right' : 'text-left'
        )}
      >
        {/* Tool calls (for assistant messages) */}
        {message.role === 'assistant' && message.toolCalls && message.toolCalls.length > 0 && (
          <div className="space-y-2">
            {message.toolCalls.map((toolCall) => (
              <ToolCallDisplay key={toolCall.id} toolCall={toolCall} />
            ))}
          </div>
        )}

        {/* Message text and code blocks */}
        <div
          className={cn(
            'rounded-lg px-3 py-2 text-sm',
            message.role === 'user'
              ? 'bg-primary text-primary-foreground inline-block'
              : 'bg-muted prose prose-sm dark:prose-invert max-w-none prose-p:break-words prose-pre:overflow-x-auto overflow-x-hidden'
          )}
          style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
        >
          {message.role === 'user' ? (
            // User messages: plain text
            <span className="whitespace-pre-wrap break-words">{message.content}</span>
          ) : (
            // Assistant messages: render as markdown
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                // Custom code block renderer with Copy/Apply buttons
                code({ node, className, children, ...props }) {
                  const match = /language-(\w+)/.exec(className || '');
                  const language = match ? match[1] : '';
                  const codeContent = String(children).replace(/\n$/, '');
                  const blockId = `code-${codeBlockCounter.current++}`;

                  // In react-markdown v9+, inline code has no className and no newlines
                  // Block code either has a language class or contains newlines
                  const isInline = !className && !codeContent.includes('\n');

                  if (isInline) {
                    return (
                      <code className="bg-zinc-800 text-zinc-200 px-1 py-0.5 rounded text-xs" {...props}>
                        {children}
                      </code>
                    );
                  }

                  const canApply = isYamlOrMarkdown(language, codeContent);

                  return (
                    <div className="my-2 rounded-md bg-zinc-900 text-zinc-100 overflow-hidden not-prose">
                      {/* Code block header */}
                      <div className="flex items-center justify-between px-3 py-1.5 bg-zinc-800 text-xs">
                        <span className="text-zinc-400">{language || 'code'}</span>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-zinc-400 hover:text-zinc-100"
                            onClick={() => handleCopy(codeContent, blockId)}
                          >
                            {copiedId === blockId ? (
                              <Check className="h-3 w-3" />
                            ) : (
                              <Copy className="h-3 w-3" />
                            )}
                          </Button>
                          {canApply && onApplyCode && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-xs text-purple-400 hover:text-purple-300 hover:bg-purple-900/30"
                              onClick={() => onApplyCode(codeContent)}
                            >
                              <Play className="h-3 w-3 mr-1" />
                              Apply
                            </Button>
                          )}
                        </div>
                      </div>
                      {/* Code content */}
                      <pre className="p-3 overflow-x-auto text-xs m-0">
                        <code>{codeContent}</code>
                      </pre>
                    </div>
                  );
                },
                // Style other markdown elements
                p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                ul: ({ children }) => <ul className="list-disc pl-4 mb-2">{children}</ul>,
                ol: ({ children }) => <ol className="list-decimal pl-4 mb-2">{children}</ol>,
                li: ({ children }) => <li className="mb-1">{children}</li>,
                h1: ({ children }) => <h1 className="text-lg font-bold mb-2">{children}</h1>,
                h2: ({ children }) => <h2 className="text-base font-bold mb-2">{children}</h2>,
                h3: ({ children }) => <h3 className="text-sm font-bold mb-1">{children}</h3>,
                strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                em: ({ children }) => <em className="italic">{children}</em>,
                a: ({ href, children }) => (
                  <a href={href} className="text-blue-500 hover:underline" target="_blank" rel="noopener noreferrer">
                    {children}
                  </a>
                ),
                blockquote: ({ children }) => (
                  <blockquote className="border-l-2 border-zinc-500 pl-3 italic my-2">{children}</blockquote>
                ),
                // Table support (requires remark-gfm)
                table: ({ children }) => (
                  <div className="overflow-x-auto my-2">
                    <table className="min-w-full border-collapse text-sm">{children}</table>
                  </div>
                ),
                thead: ({ children }) => (
                  <thead className="bg-muted/50">{children}</thead>
                ),
                tbody: ({ children }) => <tbody>{children}</tbody>,
                tr: ({ children }) => (
                  <tr className="border-b border-border">{children}</tr>
                ),
                th: ({ children }) => (
                  <th className="px-3 py-1.5 text-left font-semibold border border-border">{children}</th>
                ),
                td: ({ children }) => (
                  <td className="px-3 py-1.5 border border-border">{children}</td>
                ),
              }}
            >
              {message.content}
            </ReactMarkdown>
          )}

          {/* Streaming cursor */}
          {message.isStreaming && (
            <span className="inline-block w-2 h-4 ml-0.5 bg-current animate-pulse" />
          )}
        </div>
      </div>
    </div>
  );
}

export default AssistantMessage;
